import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Subscription } from 'rxjs';
import { REPORTING_VAAD_LIZ_REPORTS } from '../../../router/routes';
import { useService } from '../../../services/services';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
  useTab,
} from '../../../components/common/tabs/tab-container';
import ShowIfAllowed from '../../../components/common/show-if-allowed';
import Title from '../../../components/common/title';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import ReportList from './report-list';
import ReportsHistory from './history';

export default function VaadLizReportIndex() {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.vaad_liz_reports_v2.'.concat(key)),
    [t]
  );
  const { farmId } = useParams<{ farmId: string }>();
  const [year, setYear] = useTab(REPORTING_VAAD_LIZ_REPORTS, [
    'year',
    'farmId',
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const { vaadLizReportV2Service: vaadLizReportService } = useService();

  useEffect(() => {
    setIsLoading(true);

    const subscription: Subscription = vaadLizReportService
      .index(farmId)
      .subscribe((data) => {
        const transformedData = {};

        if (data.length) {
          data.forEach((row) => {
            if (!transformedData[row.season_id]) {
              transformedData[row.season_id] = [];
            }
            transformedData[row.season_id].push(row);
          });
        } else {
          transformedData[new Date().getFullYear()] = [];
        }

        setIsLoading(false);
        setData(transformedData);
      });

    return () => subscription.unsubscribe();
  }, [farmId, vaadLizReportService]);

  return isLoading || !data ? (
    <>
      <Title title={tp('title')} />
      <DashboardLoading />
    </>
  ) : (
    <>
      <Title title={tp('title')} />

      <TabContainer
        selectedTabKey={year}
        setTab={setYear}
        tabNavigation={
          <TabNavigation>
            <ShowIfAllowed ability={{ entity_type: 'vaad_liz_reports' }}>
              <TabLink tabKey={'2023'} key={'2023'}>
                2023
              </TabLink>
              {Object.keys(data)
                .sort((a, b) => parseInt(b) - parseInt(a))
                .map((season: string) => (
                  <TabLink tabKey={season} key={season}>
                    {season}
                  </TabLink>
                ))}
            </ShowIfAllowed>
          </TabNavigation>
        }
        tabContents={
          <TabContents>
            <ShowIfAllowed ability={{ entity_type: 'vaad_liz_reports' }}>
              <Tab tabKey={'2023'} key={'2023'}>
                <ReportsHistory />
              </Tab>
              {Object.keys(data)
                ?.sort((a, b) => parseInt(b) - parseInt(a))
                ?.map((season: string) => (
                  <Tab tabKey={season} key={season}>
                    <ReportList data={data[season]} year={season} />
                  </Tab>
                ))}
            </ShowIfAllowed>
          </TabContents>
        }
      />
    </>
  );
}
